import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, X, Lightbulb } from 'lucide-react';

import { cn } from '../../lib/utils';
import { getUnmentionedElements } from '../../data/roomChecklists';
import type { ChecklistElement } from '../../types';

// ================================================================= //
//          HelpfulModeChecklist — Room-Adaptive Inline Prompts       //
// ================================================================= //
// Shows only elements NOT mentioned in the room description.         //
// Each prompt is collapsible to show sub-prompts. Individual and     //
// bulk dismiss supported. Per user decision: "Templates should       //
// reduce effort, not add it."                                        //
// ================================================================= //

interface HelpfulModeChecklistProps {
  /** The canonical room type (e.g., "kitchen"). */
  roomType: string;
  /** The current free-text damage description. */
  description: string;
  /** Optional callback when all prompts are dismissed. */
  onDismiss?: () => void;
}

export const HelpfulModeChecklist: React.FC<HelpfulModeChecklistProps> = ({
  roomType,
  description,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Reason: Recompute unmentioned elements when roomType or description changes
  const unmentionedElements = useMemo(
    () => getUnmentionedElements(roomType, description),
    [roomType, description]
  );

  // Filter out dismissed elements
  const visibleElements = unmentionedElements.filter(
    (el) => !dismissed.has(el.id)
  );

  // Nothing to show
  if (visibleElements.length === 0) {
    return null;
  }

  const handleDismiss = (id: string) => {
    const newDismissed = new Set(dismissed);
    newDismissed.add(id);
    setDismissed(newDismissed);

    // Reason: Check if all prompts are now dismissed
    const remaining = unmentionedElements.filter(
      (el) => !newDismissed.has(el.id)
    );
    if (remaining.length === 0 && onDismiss) {
      onDismiss();
    }
  };

  const handleClearAll = () => {
    const allIds = new Set(unmentionedElements.map((el) => el.id));
    setDismissed(allIds);
    if (onDismiss) {
      onDismiss();
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-4">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-900">
            Did you check these areas?
          </span>
          <span className="text-xs text-amber-600">
            ({visibleElements.length} remaining)
          </span>
        </div>
        <button
          onClick={handleClearAll}
          className="text-xs font-medium text-amber-700 hover:text-amber-900 hover:underline"
        >
          Clear all
        </button>
      </div>

      {/* Checklist items */}
      <ul className="space-y-1">
        {visibleElements.map((element) => (
          <ChecklistItem
            key={element.id}
            element={element}
            isExpanded={expanded.has(element.id)}
            onToggle={() => toggleExpand(element.id)}
            onDismiss={() => handleDismiss(element.id)}
          />
        ))}
      </ul>
    </div>
  );
};

// ================================================================= //
//          ChecklistItem — Single Expandable Prompt                   //
// ================================================================= //

interface ChecklistItemProps {
  element: ChecklistElement;
  isExpanded: boolean;
  onToggle: () => void;
  onDismiss: () => void;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({
  element,
  isExpanded,
  onToggle,
  onDismiss,
}) => (
  <li className="group">
    <div className="flex items-center gap-2">
      {/* Expand/collapse toggle */}
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-amber-800 hover:bg-amber-100"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${element.label}`}
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-amber-600" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-amber-600" />
        )}
        <span className="font-medium">{element.label}</span>
      </button>

      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className={cn(
          'ml-auto rounded p-0.5 text-amber-500 hover:bg-amber-100 hover:text-amber-800',
          'opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100'
        )}
        aria-label={`Dismiss ${element.label}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>

    {/* Sub-prompts (expanded view) */}
    {isExpanded && element.sub_prompts.length > 0 && (
      <ul className="ml-7 mt-1 space-y-0.5">
        {element.sub_prompts.map((prompt, i) => (
          <li
            key={i}
            className="flex items-start gap-1.5 text-xs text-amber-700"
          >
            <span className="mt-0.5 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400" />
            {prompt}
          </li>
        ))}
      </ul>
    )}
  </li>
);

export default HelpfulModeChecklist;
